import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Award, Users, BadgeCheck, ArrowUpRight } from "lucide-react";
import { SITE, buildWaUrl } from "../data/site";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { BoltIcon } from "../components/Decorations";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="Tentang ListrikBatam.com — Teknisi Listrik Profesional Batam"
        description="Kenali tim ListrikBatam.com — teknisi bersertifikat, pengalaman 10+ tahun, sudah menangani 1.200+ proyek rumah tangga, komersial, dan industri di Batam & Kepri."
        canonical="https://listrikbatam.com/tentang"
      />

      {/* Hero */}
      <section className="relative bg-[#F8FAFC] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Tentang Kami
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black leading-[0.98] tracking-tight text-[#0F4C75] text-balance">
              Teknisi listrik
              <br /> yang <span className="text-[#FF6B00]">benar-benar</span>
              <br /> bisa diandalkan.
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4 text-[#1B262C]/75 text-base md:text-lg">
            Sejak {SITE.founded}, ListrikBatam.com hadir sebagai layanan teknis
            kelistrikan dan peralatan rumah tangga dalam satu solusi terpadu.
            Didukung tim teknisi dengan pengalaman 15+ tahun di proyek rumah,
            komersial, hingga industri.
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="relative rounded-3xl bg-[#0F4C75] text-white p-8 md:p-10 overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-20" />
              <BoltIcon className="absolute -top-4 -right-4 w-24 h-32 opacity-30" color="#FF6B00" />
              <div className="relative">
                <div className="text-[#FFB341] text-xs uppercase tracking-[0.3em] font-bold mb-3">
                  Cerita kami
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-black leading-tight">
                  Dari satu tukang ke tim {SITE.areas}+ teknisi.
                </h2>
                <p className="mt-5 text-white/80 text-sm md:text-base leading-relaxed">
                  ListrikBatam.com dimulai oleh seorang teknisi senior di
                  bengkel kecil daerah Tiban. Hari demi hari, reputasi ia bangun
                  dari pekerjaan yang rapi, estimasi yang jujur, dan garansi
                  yang benar-benar ditepati.
                </p>
                <p className="mt-4 text-white/80 text-sm md:text-base leading-relaxed">
                  Kini tim kami sudah menangani lebih dari {SITE.clients}+
                  pelanggan di Batam dan Kepri—dari rumah tinggal sampai panel
                  industri galangan kapal.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7 grid grid-cols-2 gap-5" stagger>
            {[
              { icon: Award, num: `${SITE.technicianYears}+`, label: "Tahun pengalaman teknisi" },
              { icon: Users, num: `${SITE.clients}+`, label: "Pelanggan puas" },
              { icon: ShieldCheck, num: "100%", label: "Bergaransi" },
              { icon: Clock, num: "< 30′", label: "Respon darurat" },
            ].map(({ icon: Icon, num, label }) => (
              <div
                key={label}
                className="rounded-3xl bg-white border border-[#E5E7EB] p-7 hover:-translate-y-1 hover:border-[#3282B8] transition-all"
                data-testid={`about-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-6 h-6 text-[#FF6B00] mb-5" />
                <div className="font-display text-4xl md:text-5xl font-black text-[#0F4C75]">
                  {num}
                </div>
                <div className="text-sm text-[#1B262C]/70 mt-1">{label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal className="max-w-3xl mb-12">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Nilai Kami
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] tracking-tight leading-[1.02]">
              Jujur dulu, baru teknis.
            </h2>
          </Reveal>
          <Reveal className="grid grid-cols-1 md:grid-cols-3 gap-5" stagger>
            {[
              {
                title: "Transparansi total",
                body: "Kami kirimkan rincian material, jam kerja, dan estimasi sebelum kerja dimulai. Tidak ada biaya kejutan.",
              },
              {
                title: "Tidak kompromi soal keamanan",
                body: "Kami tolak pekerjaan yang mengabaikan standar PUIL. Nyawa Anda lebih penting dari satu proyek.",
              },
              {
                title: "Bertanggung jawab sampai tuntas",
                body: "Selama garansi berlaku, kami kembali tanpa biaya tambahan kalau ada masalah yang sama.",
              },
            ].map((v) => (
              <div
                key={v.title}
                className="rounded-3xl bg-white border border-[#E5E7EB] p-7 hover:-translate-y-1 hover:border-[#FF6B00] transition-all"
              >
                <BadgeCheck className="w-6 h-6 text-[#FF6B00] mb-4" />
                <h3 className="font-display text-xl font-bold text-[#0F4C75] mb-3">
                  {v.title}
                </h3>
                <p className="text-sm text-[#1B262C]/75 leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
        <Reveal className="rounded-3xl bg-gradient-to-br from-[#0F4C75] to-[#1B262C] text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-25" />
          <BoltIcon
            className="absolute top-6 right-8 w-14 h-20 float-slow opacity-80"
            color="#FF6B00"
          />
          <h2 className="relative font-display text-3xl md:text-5xl font-black">
            Siap bekerja dengan kami?
          </h2>
          <p className="relative mt-4 max-w-xl mx-auto text-white/80">
            Hubungi via WhatsApp — kami akan respon dalam 15 menit di jam
            kerja.
          </p>
          <a
            href={buildWaUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-wa-cta"
            className="relative mt-7 inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full px-7 py-3.5"
          >
            Chat WhatsApp <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </section>
    </div>
  );
}
